import {
  CreativeReportRow,
  CreativeReportColumn,
  Creative,
  AssignedLabelsResponse,
  VideoRetentionData,
  ServerPaginationState,
  AdDetailResponse,
} from "../../types";
import CreativeCard from "./CreativeCard";
import { memo, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";
import { getVisiblePages } from "@/components/ui/data-table-pagination2";
import { RiArrowDownSLine, RiArrowLeftSLine, RiArrowRightSLine } from "@remixicon/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getAssignedLabels } from "../../apiService";

interface CreativeCardGridProps {
  data: CreativeReportRow[] | CreativeReportRow["ads"];
  columns: CreativeReportColumn[];
  ad_columns?: CreativeReportColumn[];
  setGroupSheetOpen: (open: boolean) => void;
  setSelectedAdGroup: (row: CreativeReportRow | null) => void;
  isAdView?: boolean;
  allowPagination?: boolean;
  sectionId?: string;
  scrollToSection?: (sectionId: string, overrideScroll?: boolean) => void;
  groupType?: string;
  groupByKey?: string;
  creativeType?: string;
  labelView?: string;
  labelConfigId?: string;
  accountId?: string;
  showBlankCard?: boolean;
  uniqueLabels?: string[];
  selectedDateView?: string;
  sectionMetricLabels?: { id: string; label: string }[];
  forcedAspectRatio?: string;
  serverPagination?: ServerPaginationState;
  onServerPageChange?: (page: number) => void;
  onServerPageSizeChange?: (pageSize: number) => void;
  isPaginationLoading?: boolean;
  fetchGalleryAds?: (
    groupValue: string,
    page: number,
    pageSize: number,
  ) => Promise<AdDetailResponse | null>;
}

const CARD_GAP = 14;
const MAX_CARD_WIDTH = 350; // Maximum width cap for cards
const MIN_CARD_WIDTH = 220; // Minimum width for cards

const CreativeCardGrid = memo(
  ({
    data,
    columns,
    ad_columns,
    setGroupSheetOpen,
    setSelectedAdGroup,
    isAdView = false,
    allowPagination = true,
    sectionId,
    scrollToSection,
    groupType,
    groupByKey,
    creativeType,
    labelView,
    labelConfigId,
    accountId,
    uniqueLabels,
    selectedDateView,
    sectionMetricLabels,
    forcedAspectRatio,
    serverPagination,
    onServerPageChange,
    onServerPageSizeChange,
    isPaginationLoading = false,
    fetchGalleryAds,
  }: CreativeCardGridProps) => {
    const primaryColumnKey = useMemo(
      () => columns.find((c) => c.is_primary)?.key,
      [columns],
    );

    // Manipulate data to change N/A values to Miscellaneous Ads in primary column
    // and filter out Miscellaneous rows
    const manipulatedData = useMemo(() => {
      if (!primaryColumnKey || !data) {
        return data || [];
      }
      if (Array.isArray(data)) {
        // Create a new array with the same type as the original
        const result = data
          .map((row) => {
            // Create a shallow copy to avoid mutating the original
            const newRow = { ...row };
            if (
              typeof newRow[primaryColumnKey] === "string" &&
              newRow[primaryColumnKey].trim().toUpperCase() === "N/A"
            ) {
              newRow[primaryColumnKey] = "Miscellaneous Ads";
            }
            return newRow;
          })
          .filter((row) => {
            // Filter out rows where primary column is "Miscellaneous Ads" or "Miscellaneous"
            const primaryValue = row[primaryColumnKey];
            if (typeof primaryValue === "string") {
              const normalizedValue = primaryValue.trim();
              return (
                normalizedValue !== "Miscellaneous Ads" &&
                normalizedValue !== "Miscellaneous"
              );
            }
            return true;
          });
        return result as typeof data;
      }
      return data;
    }, [data, primaryColumnKey]);

    // Silence unused warnings for props that are forwarded to children via callbacks/components
    const [gridCols, setGridCols] = useState<number | null>(null);
    const [cardWidth, setCardWidth] = useState(238);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const containerRef = useRef<HTMLDivElement>(null);
    const gridRef = useRef<HTMLDivElement>(null);

    const isUserInitiatedChange = useRef(false);

    // Add state to track selected media items for gallery view
    const [selectedGalleryItems, setSelectedGalleryItems] = useState<
      | (Creative & {
          type: "image" | "video" | "mixed";
          ad_name: string;
          ad_id?: string;
          creative_id?: string;
          videoRetentionData?: VideoRetentionData;
        } & Record<string, unknown>)[]
      | null
    >(null);

    useEffect(() => {
      let timeoutId: NodeJS.Timeout;

      const updateGridLayout = () => {
        // Clear any pending updates
        clearTimeout(timeoutId);

        // Debounce the update to prevent rapid firing
        timeoutId = setTimeout(() => {
          if (!containerRef.current) return;

          const containerWidth = containerRef.current.offsetWidth;

          // Skip update if container width is not yet measured
          if (containerWidth === 0) return;

          // Calculate how many columns can fit with minimum card width
          const minCols = Math.floor(containerWidth / (MIN_CARD_WIDTH + CARD_GAP));
          const maxCols = Math.floor(containerWidth / (MAX_CARD_WIDTH + CARD_GAP));

          // Determine number of columns (between 1 and 6)
          let newCols = Math.min(Math.max(minCols, 1), 6);

          // If we can fit more columns with max width, use that
          if (maxCols >= newCols) {
            newCols = Math.min(maxCols, 6);
          }

          // Calculate card width based on available space and number of columns
          // Account for gaps: (containerWidth - (gaps between columns)) / columns
          const totalGapWidth = CARD_GAP * (newCols - 1);
          const availableWidth = containerWidth - totalGapWidth;
          const calculatedCardWidth = availableWidth / newCols;

          // Clamp card width between min and max
          const newCardWidth = Math.min(
            Math.max(calculatedCardWidth, MIN_CARD_WIDTH),
            MAX_CARD_WIDTH,
          );

          // Only update if values actually changed to prevent unnecessary re-renders
          setGridCols((prevCols) => (prevCols !== newCols ? newCols : prevCols));
          setCardWidth((prevWidth) =>
            prevWidth !== newCardWidth ? newCardWidth : prevWidth,
          );
        }, 50); // 50ms debounce
      };

      // Create ResizeObserver to watch container size changes
      const resizeObserver = new ResizeObserver(updateGridLayout);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
        // Trigger initial update after a brief delay to ensure container is fully rendered
        setTimeout(updateGridLayout, 100);
      }

      // Still keep window resize listener for safety
      window.addEventListener("resize", updateGridLayout);

      return () => {
        clearTimeout(timeoutId);
        resizeObserver.disconnect();
        window.removeEventListener("resize", updateGridLayout);
      };
    }, []);

    // No longer need column organization for masonry - we'll use flat grid

    // Pagination calculations — for v2 the server handles slicing, so show all returned rows
    const effectiveTotalPages = serverPagination
      ? serverPagination.totalPages
      : Math.max(1, Math.ceil(manipulatedData.length / itemsPerPage));
    const totalPages = effectiveTotalPages;
    const effectiveCurrentPage = serverPagination ? serverPagination.page : currentPage;
    const startIndex = serverPagination ? 0 : (currentPage - 1) * itemsPerPage;
    const paginatedData = serverPagination
      ? manipulatedData
      : allowPagination
        ? manipulatedData.slice(startIndex, startIndex + itemsPerPage)
        : manipulatedData;

    // Track removed cards (by their unique identifier)
    const [removedCardKeys, setRemovedCardKeys] = useState<Set<string>>(new Set());

    // Helper to generate a unique key for an item
    const getItemKey = useCallback(
      (item: CreativeReportRow | CreativeReportRow["ads"][0], index: number): string => {
        if (isAdView) {
          const ad = item as CreativeReportRow["ads"][0];
          return `ad-${ad.ad_id || ad.creative_id || index}`;
        } else {
          const row = item as CreativeReportRow;
          if (primaryColumnKey) {
            const primaryValue = row[primaryColumnKey];
            const valueStr =
              typeof primaryValue === "string" || typeof primaryValue === "number"
                ? String(primaryValue)
                : "";
            return `row-${valueStr}-${index}`;
          }
          return `row-${index}`;
        }
      },
      [isAdView, primaryColumnKey],
    );

    // Filter out removed cards for flat grid layout
    const filteredData = useMemo(() => {
      // Type assertion is safe here since paginatedData is always an array
      const dataArray = paginatedData as Array<
        CreativeReportRow | CreativeReportRow["ads"][0]
      >;
      return dataArray.filter((item, index) => {
        const key = getItemKey(item, index);
        return !removedCardKeys.has(key);
      });
    }, [paginatedData, removedCardKeys, getItemKey]);

    // Generate items per page options based on grid columns.
    // For server pagination, lock to the first measured gridCols so resize doesn't shuffle the dropdown.
    const lockedGridColsRef = useRef<number | null>(null);
    if (serverPagination && gridCols && !lockedGridColsRef.current) {
      lockedGridColsRef.current = gridCols;
    }
    const effectiveCols = serverPagination ? (lockedGridColsRef.current ?? gridCols) : gridCols;
    const itemsPerPageOptions = useMemo(
      () => (effectiveCols ? Array.from({ length: 8 }, (_, i) => (i + 1) * effectiveCols) : []),
      [effectiveCols],
    );
    useEffect(() => {
      if (!gridCols || serverPagination) return;
      const closestValidItems = itemsPerPageOptions.reduce((prev, curr) =>
        Math.abs(curr - itemsPerPage) < Math.abs(prev - itemsPerPage) ? curr : prev,
      );
      const minimumTwoRows = 2 * gridCols;
      const nextItemsPerPage = Math.max(closestValidItems, minimumTwoRows);
      setItemsPerPage(nextItemsPerPage);
      setCurrentPage(1);
      isUserInitiatedChange.current = false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridCols]);

    // No longer need max height calculation for equal-sized cards

    const handlePageChange = (newPage: number) => {
      isUserInitiatedChange.current = true;
      if (serverPagination && onServerPageChange) {
        onServerPageChange(newPage);
      } else {
        setCurrentPage(newPage);
      }
    };

    const handleItemsPerPageChange = (value: string) => {
      isUserInitiatedChange.current = true;
      const newSize = Number(value);
      setItemsPerPage(newSize);
      if (serverPagination && onServerPageSizeChange) {
        onServerPageSizeChange(newSize);
      } else {
        setCurrentPage(1);
      }
    };

    useEffect(() => {
      if (isUserInitiatedChange.current && sectionId && scrollToSection) {
        scrollToSection(sectionId, true);
        isUserInitiatedChange.current = false;
      }
    }, [currentPage, itemsPerPage, sectionId, scrollToSection]);

    // Helper to compute retention data for a single ad
    const computeRetentionData = (
      ad: CreativeReportRow["ads"][0],
    ): VideoRetentionData | undefined => {
      if (!ad_columns) return undefined;

      const retentionColumns = ad_columns.filter(
        (col) => col.column_type === "media_data_node" && col.percentage !== undefined,
      );

      if (retentionColumns.length === 0) return undefined;

      const retentionData: VideoRetentionData = [];

      retentionColumns.forEach((col) => {
        const value = ad[col.key];
        if (typeof value === "number" && !isNaN(value)) {
          retentionData.push({
            percentage: col.percentage!,
            retention: value,
          });
        } else if (typeof value === "string" && value !== "" && value !== "N/A") {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            retentionData.push({
              percentage: col.percentage!,
              retention: numValue,
            });
          }
        }
      });

      return retentionData.length > 0
        ? retentionData.sort((a, b) => a.percentage - b.percentage)
        : undefined;
    };

    // Function to handle opening gallery view
    const handleGalleryOpen = (items: CreativeReportRow["ads"]) => {
      if (!items || items.length === 0) return;
      setSelectedGalleryItems(
        items.map((item) => {
          // Extract winning criteria values from ad
          const winningCriteriaData: Record<string, boolean> = {};
          if (ad_columns) {
            const winningColumns = ad_columns.filter(
              (col) =>
                (col as CreativeReportColumn & { winning_criteria_name?: string })
                  .winning_criteria_name,
            );
            winningColumns.forEach((col) => {
              const value = (item as Record<string, unknown>)[col.key];
              if (value === true) {
                winningCriteriaData[col.key] = true;
              }
            });
          }

          return {
            ...item.creative,
            type: item.creative_type,
            ad_name: item.ad_name || "",
            ad_id: item.ad_id || "",
            creative_id: item.creative_id || "",
            videoRetentionData:
              item.creative_type === "video" ? computeRetentionData(item) : undefined,
            ...winningCriteriaData, // Include winning criteria fields
          };
        }),
      );
    };

    const removeCard = useCallback(
      (colIndex: number, rowIndex: number) => {
        if (!gridCols) return;
        // Find the item at this position in the filtered data
        // Calculate the flat index from colIndex and rowIndex
        const flatIndex = rowIndex * gridCols + colIndex;
        const item = filteredData[flatIndex];
        if (!item) return;

        // Find the original index in paginatedData
        const originalIndex = paginatedData.findIndex((dataItem) => {
          if (isAdView) {
            return (
              (dataItem as CreativeReportRow["ads"][0]).ad_id ===
              (item as CreativeReportRow["ads"][0]).ad_id
            );
          } else {
            const primaryKey = primaryColumnKey || "";
            return (
              (dataItem as CreativeReportRow)[primaryKey] ===
              (item as CreativeReportRow)[primaryKey]
            );
          }
        });

        if (originalIndex === -1) return;

        // Create a unique key for this item
        const key = getItemKey(item, originalIndex);

        // Add to removed set (this will trigger filteredData recomputation)
        setRemovedCardKeys((prev) => new Set(prev).add(key));
      },
      [filteredData, paginatedData, isAdView, primaryColumnKey, getItemKey, gridCols],
    );
    const [labelData, setLabelData] = useState<AssignedLabelsResponse | null>(null);

    // Fetch labels when component mounts and when labelConfigId changes
    useEffect(() => {
      const fetchLabels = async () => {
        if (!accountId || !labelConfigId || groupType !== "text_ai_label") return;
        try {
          const response = await getAssignedLabels(accountId, labelConfigId);
          setLabelData(response);
        } catch (error) {
          console.error("Error fetching labels:", error);
        }
      };

      void fetchLabels();
    }, [accountId, groupType, labelConfigId]);

    return (
      <div className="flex flex-col w-full gap-4">
        <div className="w-full flex justify-start" ref={containerRef}>
          <div
            className={cn(
              "grid transition-opacity duration-200",
              isPaginationLoading && "opacity-50 pointer-events-none",
            )}
            ref={gridRef}
            style={{
              gridTemplateColumns: `repeat(${gridCols}, ${cardWidth}px)`,
              gridAutoRows: "1fr",
              gap: `${CARD_GAP}px`,
            }}
          >
            {filteredData.map((item, index) => {
              // Calculate colIndex and rowIndex from flat index
              if (!gridCols) return null;
              const colIndex = index % gridCols;
              const rowIndex = Math.floor(index / gridCols);

              return (
                <div
                  key={getItemKey(item, index)}
                  className="creative-card"
                  style={{ width: `${cardWidth}px` }}
                >
                  <CreativeCard
                    data={item}
                    columns={columns}
                    ad_columns={ad_columns}
                    onShopNowClick={() => {
                      const url = isAdView
                        ? (item as CreativeReportRow["ads"][0]).website_url
                        : item.website_url;
                      if (url) window.open(url as string, "_blank");
                    }}
                    onPanelClick={() => {
                      if (!isAdView && "ads" in item) {
                        setSelectedAdGroup(item as CreativeReportRow);
                        setGroupSheetOpen(true);
                      }
                    }}
                    onGalleryOpen={() =>
                      handleGalleryOpen(item.ads as CreativeReportRow["ads"])
                    }
                    isAdView={isAdView}
                    showCardHeader={!isAdView}
                    groupType={groupType}
                    groupByKey={groupByKey}
                    labelConfigId={labelConfigId}
                    accountId={accountId}
                    selectedDateView={selectedDateView}
                    selectedGalleryItems={selectedGalleryItems}
                    onGalleryClose={() => setSelectedGalleryItems(null)}
                    creativeType={creativeType}
                    labelData={labelData}
                    setLabelData={setLabelData}
                    labelView={labelView}
                    uniqueLabels={uniqueLabels}
                    removeCard={removeCard}
                    colIndex={colIndex}
                    rowIndex={rowIndex}
                    sectionId={sectionId}
                    sectionMetricLabels={sectionMetricLabels}
                    forcedAspectRatio={forcedAspectRatio}
                    containerWidth={cardWidth}
                    fetchGalleryAds={fetchGalleryAds}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Only show pagination controls if allowPagination is true and more than 1 page */}
        {allowPagination && totalPages > 1 && (
          <div
            className="flex items-center px-0 py-2"
            style={{
              maxWidth: gridCols
                ? gridCols * cardWidth + CARD_GAP * (gridCols - 1)
                : "100%",
            }}
          >
            <div className="flex items-center justify-between w-full space-x-6 lg:space-x-8">
              <div className="flex items-center space-x-2">
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={handleItemsPerPageChange}
                >
                  <SelectTrigger
                    chevronClassName="hidden"
                    className="h-8 rounded-[8px] bg-white hover:bg-hover-gray flex items-center border-0 squircle squircle-outline-[1px] squircle-outline-color-border-gray"
                  >
                    <p className="text-[11px] font-normal text-[#67749B] mr-1">
                      Groups per page{": "}
                    </p>
                    <span className="text-[11px] font-medium mr-1 text-brand-primary-new/80">
                      {itemsPerPage}
                    </span>
                    <RiArrowDownSLine size={14} color="#9aa3bd" className="shrink-0" />
                  </SelectTrigger>
                  <SelectContent side="top" className="rounded-[8px]">
                    {itemsPerPageOptions.map((option) => (
                      <SelectItem
                        key={option}
                        value={option.toString()}
                        className="rounded-[4px] text-[#35467A] data-[state=checked]:font-medium [&>span:first-child]:data-[state=checked]:text-[#FF4500]"
                      >
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-[6px]">
                <Button
                  variant="sq-outline"
                  className="h-8 w-8 p-0 bg-white hover:bg-hover-gray transition-opacity duration-200"
                  onClick={() => handlePageChange(Math.max(1, effectiveCurrentPage - 1))}
                  disabled={effectiveCurrentPage === 1}
                >
                  <span className="sr-only">Go to previous page</span>
                  <RiArrowLeftSLine className="h-4 w-4" color="#9aa3bd" />
                </Button>

                <AnimatePresence mode="popLayout">
                  {getVisiblePages(effectiveCurrentPage, totalPages).map((item, index) =>
                    item === "ellipsis" ? (
                      <motion.span
                        key={`ellipsis-${index}`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.15 }}
                        className="flex h-8 w-8 items-center justify-center text-[12px] text-[#35467A] select-none"
                      >
                        &hellip;
                      </motion.span>
                    ) : (
                      <motion.button
                        key={item}
                        layout
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.15 }}
                        onClick={() => handlePageChange(item)}
                        className={cn(
                          "relative h-8 w-8 rounded-[8px] text-[12px] transition-colors duration-200 ease-in-out",
                          item === effectiveCurrentPage
                            ? "text-[#FF4500] font-medium"
                            : "text-[#35467A] font-normal hover:bg-hover-gray",
                        )}
                      >
                        {item === effectiveCurrentPage && (
                          <motion.div
                            layoutId="card-active-page-bg"
                            className="absolute inset-0 rounded-[8px] bg-[#FFECE4]"
                            transition={{ type: "spring", stiffness: 350, damping: 30 }}
                          />
                        )}
                        <span className="relative z-10">{item}</span>
                      </motion.button>
                    ),
                  )}
                </AnimatePresence>

                <Button
                  variant="sq-outline"
                  className="h-8 w-8 p-0 bg-white hover:bg-hover-gray transition-opacity duration-200"
                  onClick={() =>
                    handlePageChange(Math.min(totalPages, effectiveCurrentPage + 1))
                  }
                  disabled={effectiveCurrentPage === totalPages}
                >
                  <span className="sr-only">Go to next page</span>
                  <RiArrowRightSLine className="h-4 w-4" color="#9aa3bd" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

CreativeCardGrid.displayName = "CreativeCardGrid";

export default CreativeCardGrid;
